import { useCallback, useEffect, useState } from 'react';
import type { AxiosError } from 'axios';

import type { ModuleOrPlanActionOrgUnit } from 'src/types/orgUnit';
import type { ActionPlan } from 'src/types/planAction';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import history from 'src/services/routing/history';
import { routePaths } from 'src/services/routing/routePaths';

export interface ActionPlanCacheReturnProps {
  actionPlans: ActionPlan[];
  changeCachedActionPlan: (actionPlanId: number, setter: any) => void;
  deleteActionPlan: (actionPlanId: number, setter?: any) => void;
  errors: any;
  fetchActionPlansForSelectedOrgUnit: (orgUnitId: number) => void;
  loadingActionPlans: boolean;
  resetActionPlanCache: () => void;
  resetErrors: () => void;
  saveActionPlan: (actionPlan: ActionPlan) => void;
}

export const useActionPlanCache = (
  reloadPlanActionStatus: () => void,
  selectedOrgUnit?: ModuleOrPlanActionOrgUnit,
): ActionPlanCacheReturnProps => {
  const [actionPlanCache, setActionPlanCache] = useState<any>({});
  const [actionPlans, setActionPlans] = useState([]);
  const [loadingActionPlans, setLoadingActionPlans] = useState(false);
  const [errors, setErrors] = useState<unknown>(null);

  const resetErrors = useCallback(() => setErrors(null), []);
  const resetActionPlanCache = useCallback(() => {
    setActionPlanCache({});
    setActionPlans([]);
    setLoadingActionPlans(false);
    setErrors(null);
  }, []);

  const changeCachedActionPlan = useCallback(
    (actionPlanId: number, setter: any) => {
      if (!selectedOrgUnit) return;
      const orgUnitId = selectedOrgUnit.id;
      const plansMapper =
        typeof setter === 'function'
          ? (old: any) => (old.id !== actionPlanId ? old : setter(old))
          : (old: any) => (old.id !== actionPlanId ? old : setter);
      setActionPlanCache((prev: any) => ({
        ...prev,
        [orgUnitId]: prev[orgUnitId].map(plansMapper),
      }));
    },
    [selectedOrgUnit],
  );

  const updateActionPlanCache = useCallback(
    (orgUnitId: number, plans: any[]) => {
      setActionPlanCache((prevState: any) => ({
        ...prevState,
        [orgUnitId]: plans,
      }));
    },
    [],
  );

  const fetchActionPlansForSelectedOrgUnit = useCallback(
    (orgUnitId: number) => {
      setLoadingActionPlans(true);

      HttpService.get(
        `${apiPaths.PLAN_ACTION.LIST_PLANS}${orgUnitId}/`,
        {},
        true,
      )
        .then((response) => updateActionPlanCache(orgUnitId, response.data))
        .catch(() => updateActionPlanCache(orgUnitId, []))
        .finally(() => {
          setLoadingActionPlans(false);
        });
    },
    [updateActionPlanCache],
  );

  const createActionPlanForOrgUnit = useCallback(
    (params: object) => {
      if (!selectedOrgUnit) return Promise.reject();
      return HttpService.post(
        `${apiPaths.PLAN_ACTION.ACTION_PLAN}`,
        params,
      ).then((response) => {
        const newActionPlanArray = [
          response.data,
          ...actionPlanCache[selectedOrgUnit.id],
        ];
        updateActionPlanCache(selectedOrgUnit.id, newActionPlanArray);
        history.replace(
          `${routePaths.PLAN_ACTION_OVERVIEW}${response.data.id}/`,
        );
      });
    },
    [selectedOrgUnit, actionPlanCache, updateActionPlanCache],
  );

  const updateActionPlan = useCallback(
    (actionPlan: ActionPlan) => {
      if (!selectedOrgUnit) return Promise.reject();
      return HttpService.patch(
        `${apiPaths.PLAN_ACTION.ACTION_PLAN}${actionPlan.id}/`,
        actionPlan,
      ).then((response) => {
        const newActionPlanArray = actionPlanCache[selectedOrgUnit.id].map(
          (_actionPlan: ActionPlan) =>
            _actionPlan.id !== actionPlan.id ? _actionPlan : response.data,
        );
        updateActionPlanCache(selectedOrgUnit.id, newActionPlanArray);
      });
    },
    [actionPlanCache, selectedOrgUnit, updateActionPlanCache],
  );

  const saveActionPlan = useCallback(
    (actionPlan: ActionPlan) => {
      if (!selectedOrgUnit || !actionPlan) return;

      (actionPlan.id === null || actionPlan.id === undefined
        ? createActionPlanForOrgUnit({
            org_unit: selectedOrgUnit.id,
            name: actionPlan.name,
            actions: actionPlan.actions,
          })
        : updateActionPlan(actionPlan)
      )
        .then(() => {
          setErrors(null);
          reloadPlanActionStatus();
        })
        .catch((error: AxiosError) => {
          if (error.response && error.response.status === 400) {
            setErrors(error.response.data);
          }
        });
    },
    [
      selectedOrgUnit,
      createActionPlanForOrgUnit,
      updateActionPlan,
      reloadPlanActionStatus,
    ],
  );

  const deleteActionPlan = useCallback(
    (actionPlanId: number) => {
      HttpService.delete(
        `${apiPaths.PLAN_ACTION.ACTION_PLAN}${actionPlanId}/`,
        {},
      )
        .then(() => {
          if (!selectedOrgUnit) return;
          const filteredCache = actionPlanCache[selectedOrgUnit.id].filter(
            (actionPlan: ActionPlan) => actionPlan.id !== actionPlanId,
          );
          updateActionPlanCache(selectedOrgUnit.id, filteredCache);
        })
        .then(() => {
          reloadPlanActionStatus();
          history.replace(routePaths.PLAN_ACTION_OVERVIEW);
        });
    },
    [
      actionPlanCache,
      updateActionPlanCache,
      selectedOrgUnit,
      reloadPlanActionStatus,
    ],
  );

  useEffect(() => {
    if (!selectedOrgUnit) return;

    if (Array.isArray(actionPlanCache[selectedOrgUnit.id])) {
      setActionPlans(actionPlanCache[selectedOrgUnit.id]);
      return;
    }

    // only fetch action plans when selectedOrgUnit is set and hasn't already been fetched.
    fetchActionPlansForSelectedOrgUnit(selectedOrgUnit.id);
  }, [selectedOrgUnit, actionPlanCache, fetchActionPlansForSelectedOrgUnit]);

  return {
    actionPlans,
    changeCachedActionPlan,
    deleteActionPlan,
    errors,
    fetchActionPlansForSelectedOrgUnit,
    loadingActionPlans,
    resetActionPlanCache,
    resetErrors,
    saveActionPlan,
  };
};
